import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  clearTaxCertApplication,
  setTaxCertApplicationLoading,
} from '../../../store/slices/taxCertificate/taxCertificateSlice';
import {
  selectTaxCertApplicationError,
  selectTaxCertApplicationFinished,
  selectTaxCertApplicationLoading,
  selectTaxCertApplicationData,
} from '../../../store/slices/taxCertificate/taxCertificateSelectors';
import {
  selectConfigExpressTaxCertificate,
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressApplyRentalStructureRequired,
} from '../../../store/slices/config/configSelectors';
import { confirmTaxCertificate } from '../../../store/slices/taxCertificate/taxCertificateThunks';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { FLOWS_ENUM } from '../../../common/enums';
import { ConfirmationPage } from 'src/components/pages/ConfirmationPage';
import { RegistrationFlow } from 'src/types/configuration';
import { useAppDispatch } from 'src/store/store';
import { ApplicationData } from 'src/types/flow';

const TaxCertConfirmation = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const taxCertConfig = useSelector(selectConfigExpressTaxCertificate);
  const [errorMessage, setErrorMessage] = useState('');

  const applicationLoading = useSelector(selectTaxCertApplicationLoading);
  const applicationFinished = useSelector(selectTaxCertApplicationFinished);
  const applicationError = useSelector(selectTaxCertApplicationError);
  const applicationData = useSelector(selectTaxCertApplicationData);

  const localContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );
  const permitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const parcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
  const rentalStructureRequiredStatus = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );

  useEffect(() => {
    if (applicationError) {
      setErrorMessage(applicationError);
    }
  }, [applicationError]);

  useEffect(() => {
    if (applicationFinished) {
      placeNavigate('/tax-cert-success');
      dispatch(setTaxCertApplicationLoading(false));
    }
  }, [applicationFinished, dispatch, placeNavigate]);

  const onGoBack = useCallback(() => {
    dispatch(clearTaxCertApplication());
  }, [dispatch]);

  const onConfirmation = useCallback(
    (data: ApplicationData) => {
      if (!applicationLoading) {
        dispatch(confirmTaxCertificate(data));
      }
    },
    [applicationLoading, dispatch]
  );

  if (!applicationData) return null;

  return (
    <ConfirmationPage
      config={taxCertConfig as RegistrationFlow}
      onGoBack={onGoBack}
      data={applicationData}
      documentUploadErrors={[applicationError]}
      flow={FLOWS_ENUM.TAX_CERTIFICATE}
      flowError={errorMessage}
      hideDocuments
      instructionsText="Please confirm the following information and sign below."
      loading={applicationLoading}
      localContactVerbiage={localContactVerbiage}
      onConfirmation={onConfirmation}
      parcelVerbiage={parcelVerbiage}
      permitTypeVerbiage={permitTypeVerbiage}
      rentalStructureRequiredStatus={rentalStructureRequiredStatus}
    />
  );
};

export default TaxCertConfirmation;
